import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import LoadingAnimation from './LoadingAnimation';
import AccessDenied from './AccessDenied';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability } from '../utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** @deprecated Prefer requiredFeature — kept only for Feature Access (hardcoded admin). */
  requiredRole?: string[];
  /** Any of these feature keys from user.allowed_features grants access. */
  requiredFeature?: string[];
  /** Optional custom title for AccessDenied */
  deniedTitle?: string;
  /** Optional custom message for AccessDenied */
  deniedMessage?: string;
}

export function getSectionDeniedMessage(pathname: string): string {
  if (pathname.startsWith('/projects/create')) return 'You do not have permission to access Project Creation.';
  if (pathname.startsWith('/sales')) return 'You do not have permission to access Sales.';
  if (pathname.startsWith('/hr/documents')) return 'You do not have permission to access My Documents.';
  if (pathname.startsWith('/hr')) return 'You do not have permission to access HR.';
  if (pathname.startsWith('/clients')) return 'You do not have permission to access Contact List.';
  if (pathname.startsWith('/users')) return 'You do not have permission to access User Management.';
  if (pathname.startsWith('/third-parties')) return 'You do not have permission to access Third Parties.';
  if (pathname.startsWith('/general-config')) return 'You do not have permission to access General Configuration.';
  if (pathname.startsWith('/feature-permissions')) return 'You do not have permission to access Feature Access.';
  if (pathname.startsWith('/permissions')) return 'You do not have permission to access Project Permissions.';
  if (pathname.startsWith('/pmo')) return 'You do not have permission to access PMO.';
  if (pathname.startsWith('/cfos')) return 'You do not have permission to access CFOS.';
  if (pathname.startsWith('/ai-chat')) return 'You do not have permission to access AI Assistant.';
  if (pathname.startsWith('/pdf-editor')) return 'You do not have permission to access PDF Editor.';
  if (pathname.startsWith('/help')) return 'You do not have permission to access Help.';
  if (pathname.startsWith('/wc2026')) return 'You do not have permission to access WC2026.';
  return 'You do not have permission to access this page.';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = [],
  requiredFeature = [],
  deniedTitle,
  deniedMessage,
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: 'background.default' }}
      >
        <LoadingAnimation />
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasFeatureAccess =
    requiredFeature.length === 0 ||
    requiredFeature.some((key) => hasCapability(user, key));

  const hasRoleAccess =
    requiredRole.length === 0 ||
    requiredRole.some((role) => user.roles.includes(role as any));

  // If both specified, both must pass (Feature Access uses role-only).
  // If only one specified, that one must pass.
  const deniedByFeature = requiredFeature.length > 0 && !hasFeatureAccess;
  const deniedByRole = requiredRole.length > 0 && !hasRoleAccess;

  if (deniedByFeature || deniedByRole) {
    return (
      <AccessDenied
        title={deniedTitle ?? 'Access denied'}
        message={deniedMessage ?? getSectionDeniedMessage(location.pathname)}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
